import { Clock, Effect } from 'effect';
import { structuredSchemaCacheKey } from '../structured-schema';
import type { StructuredSchema } from '../types';

/** Cache decoded values within the transport owner, without retaining discarded clients. */
export function createQueryCache<A>() {
  let scopes = new WeakMap<object, Map<string, { value: A; timestamp: number }>>();
  return {
    get(scope: object, key: string, lifetime: number) {
      return Effect.gen(function* () {
        const now = yield* Clock.currentTimeMillis;
        const entries = scopes.get(scope);
        const entry = entries?.get(key);
        if (!entry) return undefined;
        if (now - entry.timestamp > lifetime) {
          entries!.delete(key);
          return undefined;
        }
        return entry.value;
      });
    },
    put(scope: object, key: string, value: A, isCurrent: () => boolean) {
      return Effect.gen(function* () {
        const timestamp = yield* Clock.currentTimeMillis;
        if (!isCurrent()) return;
        let entries = scopes.get(scope);
        if (!entries) scopes.set(scope, (entries = new Map()));
        entries.set(key, { value, timestamp });
      });
    },
    delete(scope: object, key: string) {
      scopes.get(scope)?.delete(key);
    },
    clear() {
      scopes = new WeakMap();
    },
  };
}

const parserIds = new WeakMap<object, number>();
let nextParserId = 0;

/** Equal JSON schemas can share; distinct Zod parsers may apply different refinements. */
export function querySchemaKey(schema?: StructuredSchema<unknown>): string {
  if (!schema) return '';
  const key = structuredSchemaCacheKey(schema);
  if (typeof (schema as { safeParse?: unknown }).safeParse !== 'function') return key;
  let id = parserIds.get(schema);
  if (id === undefined) parserIds.set(schema, (id = ++nextParserId));
  return `${key}::parser:${id}`;
}
